//Copyright (c) 2024 Greener Power Solutions.
//This file is part of Greener Power Solutions' software,
//and may not be altered, copied, redistributed, or sold
//without specific permission.
import { useCallback, useRef, useState } from 'react';
import { useUrlState } from 'hooks/useUrlState';
import { DefaultDataItem, FilterValue, Filters, UseFilterParams } from './useFilter.types';
import {
  generateQueryString,
  initializeFiltersFromUrlState,
  processFilter,
  queryStringToUrlState,
} from './useFilter.utils';

/**
 * Custom hook to manage filters, including URL state sync.
 *
 * @template T - The type of data items being filtered.
 * @param {Object} UseFilterParams - Parameters for filter initialization.
 * @returns {Object} Object containing methods and states to manage filters.
 */
export const useFilter = <T extends object = DefaultDataItem>({
  isValidFilterProperty = (property: string) => !!property,
}: UseFilterParams = {}): {
  updateFilters: (newFilters: Filters) => void;
  clearFilter: (filterKey: string) => void;
  getFilteredData: (data: T[]) => T[];
  clearAllFilters: () => void;
  getFilterValues: (filterKeys: string | string[]) => FilterValue[];
  getFilters: () => Filters;
  applyFilters: () => void;
} => {
  const { setUrlState, getUrlState, clearUrlState } = useUrlState();
  const urlState = getUrlState();

  const [filters, setFilters] = useState<Filters>(() => initializeFiltersFromUrlState(urlState, isValidFilterProperty));
  const filtersRef = useRef(filters);

  const updateFilters = useCallback(
    (newFilters: Filters) => {
      const updatedFilters = [
        ...filtersRef.current.filter(
          (existingFilter) => !newFilters.some((newFilter) => newFilter.filterKey === existingFilter.filterKey)
        ),
        ...newFilters,
      ];

      const queryString = generateQueryString(updatedFilters);
      filtersRef.current = updatedFilters;

      setFilters(updatedFilters);
      setUrlState(queryStringToUrlState(queryString));
    },
    [setUrlState]
  );

  const clearFilter = useCallback(
    (filterKey: string | string[]) => {
      const keys = Array.isArray(filterKey) ? filterKey : [filterKey];
      const updatedFilters = filtersRef.current.filter((filter) => !keys.includes(filter.filterKey));

      const queryString = generateQueryString(updatedFilters);
      filtersRef.current = updatedFilters;

      const clearedKeys = Object.fromEntries(keys.map((key) => [key, null]));

      setFilters(updatedFilters);
      setUrlState({ ...clearedKeys, ...queryStringToUrlState(queryString) });
    },
    [setUrlState]
  );

  const applyFilters = useCallback(() => {
    if (filtersRef.current.length > 0) {
      const queryString = generateQueryString(filtersRef.current);
      setUrlState(queryStringToUrlState(queryString));
    }
  }, [setUrlState]);

  const clearAllFilters = useCallback(() => {
    filtersRef.current = [];
    setFilters([]);
    clearUrlState();
  }, [clearUrlState]);

  const getFilterValues = useCallback(
    (filterKeys: string | string[]) => {
      const keys = Array.isArray(filterKeys) ? filterKeys : [filterKeys];
      return filters.filter((filter) => keys.includes(filter.filterKey)).map((filter) => filter.value);
    },
    [filters]
  );

  const getFilters = useCallback(() => {
    return [...filters]; // Return a copy of the filters array
  }, [filters]);

  const getFilteredData = useCallback(
    (data: T[]) =>
      data?.filter((item) =>
        filters.every(({ value, compareFnKey, property }) =>
          compareFnKey && property ? processFilter(item, value, compareFnKey, property) : true
        )
      ),
    [filters]
  );

  return {
    getFilters,
    getFilteredData,
    updateFilters,
    clearFilter,
    clearAllFilters,
    getFilterValues,
    applyFilters,
  };
};
